import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { CdkMenuModule } from '@angular/cdk/menu';
import { MatIconModule } from '@angular/material/icon';
import { combineLatest, filter, takeUntil } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { FlexyooLetDirective } from '@flexyoo/shared/directives';
import { TranslateModule } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgOnDestroy } from 'src/app/shared/ng-on-destroy.service';
import { Actions, ofType } from '@ngrx/effects';
import { UserNotificationsActions, userNotificationsSelectors } from '@flexyoo/user/store';
import { NotificationsSidebarService } from './services/notifications-sidebar.service';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { UserNotificationsService } from '@flexyoo/shared/services';
import { FlexyooNotificationsService } from '@flexyoo/common';
import { MatIconButton } from '@angular/material/button';

@Component({
  selector: 'app-hr-notifications-dropdown',
  templateUrl: './hr-notifications-dropdown.component.html',
  styleUrl: './hr-notifications-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkMenuModule,
    MatIconModule,
    AsyncPipe,
    FlexyooLetDirective,
    TranslateModule,
    MatIconButton,
  ],
  providers: [NgOnDestroy],
})
export class HrNotificationsDropdownComponent implements OnInit {
  private readonly hrUrls = [
    '/hr/employees',
    '/hr/transactions-history',
    '/hr/budgets-management',
    '/hr/benefits',
    '/hr/voting-period',
    '/hr/payroll-management',
  ];

  private readonly router = inject(Router);
  private readonly onDestroy$ = inject(NgOnDestroy, { self: true });
  private readonly actions = inject(Actions);
  private readonly store = inject(Store);

  private readonly notificationsSidebarService = inject(NotificationsSidebarService);
  private readonly userNotificationsService = inject(UserNotificationsService);

  protected readonly isSilentModeOn$ = inject(FlexyooNotificationsService).isSilentModeEnabled$;

  public toggleNotificationsSidebar(): void {
    this.notificationsSidebarService.toggle();
  }

  protected readonly notificationsCount$ = combineLatest([
    this.store.select(userNotificationsSelectors.selectHrAlertsCount),
    this.store.select(userNotificationsSelectors.selectUserSidebarNotificationsCount),
  ]).pipe(
    map(([alertCount, notifsCount]) => alertCount + notifsCount),
    distinctUntilChanged()
  );

  public ngOnInit(): void {
    this.router.events
      .pipe(
        filter((ev) => ev instanceof NavigationEnd && this.hrUrls.includes(ev.url)),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => this.userNotificationsService.reloadHrAlerts());

    this.actions
      .pipe(
        ofType(UserNotificationsActions.newUserNotificationReceived),
        filter((action) => action?.notification?.role === 'HR'),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => this.userNotificationsService.reloadHrAlerts());
  }
}
